import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Landmark, Save } from 'lucide-react';

export default function ITEditForm({ record, onSave }) {
  var initType = record.income_tax_type || 'none';
  var typeState = useState(initType);
  var itType = typeState[0];
  var setItType = typeState[1];

  var amountState = useState(String(record.income_tax_amount || ''));
  var amount = amountState[0];
  var setAmount = amountState[1];

  var pctState = useState(String(record.income_tax_percent || ''));
  var pct = pctState[0];
  var setPct = pctState[1];

  var netPayable = record.net_payable || record.final_payable_salary || 0;

  function getPreview() {
    if (itType === 'fixed') return parseFloat(amount) || 0;
    if (itType === 'percent') return Math.round(netPayable * (parseFloat(pct) || 0) / 100);
    return 0;
  }

  var preview = getPreview();
  var bankPreview = Math.max(0, netPayable - preview);

  function fmt(n) {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
  }

  return (
    <div className="space-y-4" data-testid="it-edit-form">
      <div className="flex items-center gap-2">
        <Landmark className="h-4 w-4 text-indigo-600" />
        <h4 className="font-semibold text-sm">Income Tax Deduction</h4>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs text-muted-foreground">Deduction Type</Label>
          <Select value={itType} onValueChange={setItType}>
            <SelectTrigger className="h-8 text-sm mt-1" data-testid="it-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No IT Deduction</SelectItem>
              <SelectItem value="fixed">Fixed Amount</SelectItem>
              <SelectItem value="percent">Percentage of Net</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {itType === 'fixed' && (
          <div>
            <Label className="text-xs text-muted-foreground">IT Amount</Label>
            <Input
              type="number"
              value={amount}
              onChange={function(e) { setAmount(e.target.value); }}
              className="h-8 text-sm mt-1 font-mono"
              placeholder="Enter amount"
              data-testid="it-amount-input"
            />
          </div>
        )}

        {itType === 'percent' && (
          <div>
            <Label className="text-xs text-muted-foreground">IT Percentage (%)</Label>
            <Input
              type="number"
              value={pct}
              onChange={function(e) { setPct(e.target.value); }}
              className="h-8 text-sm mt-1 font-mono"
              placeholder="e.g. 10"
              data-testid="it-percent-input"
            />
          </div>
        )}

        {itType !== 'none' && (
          <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 border">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Net Payable</span>
              <span className="font-mono">{fmt(netPayable)}</span>
            </div>
            <div className="flex justify-between text-xs text-indigo-600">
              <span>Income Tax</span>
              <span className="font-mono">-{fmt(preview)}</span>
            </div>
            <div className="border-t pt-1.5 flex justify-between text-sm font-semibold text-emerald-700">
              <span>Bank Transfer</span>
              <span className="font-mono">{fmt(bankPreview)}</span>
            </div>
          </div>
        )}
      </div>

      <Button
        size="sm"
        className="w-full gap-1.5"
        onClick={function() { onSave(itType, amount, pct); }}
        data-testid="it-save-btn"
      >
        <Save className="h-3.5 w-3.5" /> Save
      </Button>
    </div>
  );
}
